import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import Windows95Modal from '../shared/Windows95Modal';
import baelogo from "../../assets/baesystemslogo.png";
import boeinglogo from "../../assets/boeinglogo.png";

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
const loader = new GLTFLoader();
loader.setDRACOLoader(dracoLoader);

function createViewer(wrapEl, modelPath, offsetY = 0, offsetX = 0) {
  const scene = new THREE.Scene();

  const starGeo = new THREE.BufferGeometry();
  const starCount = 2000;
  const starPos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount * 3; i++) starPos[i] = (Math.random() - 0.5) * 2000;
  starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
  scene.add(new THREE.Points(starGeo, new THREE.PointsMaterial({
    color: 0xffffff, size: 0.5, sizeAttenuation: true, transparent: true, opacity: 0.7,
  })));

  const camera = new THREE.PerspectiveCamera(45, wrapEl.clientWidth / wrapEl.clientHeight, 0.01, 5000);
  camera.position.set(0, 0, 7);
  camera.lookAt(0, 0, 0);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setClearColor(0x000000, 0);
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.2;
  wrapEl.appendChild(renderer.domElement);

  function resize() {
    const w = wrapEl.clientWidth;
    const h = wrapEl.clientHeight;
    renderer.setSize(w, h);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  resize();

  scene.add(new THREE.AmbientLight(0x334466, 1.5));
  const sun = new THREE.DirectionalLight(0xfff5e0, 4);
  sun.position.set(10, 8, 5);
  scene.add(sun);
  const fill = new THREE.DirectionalLight(0x4488cc, 0.8);
  fill.position.set(-8, -4, -6);
  scene.add(fill);

  let model = null;
  const drag = { active: false, prevX: 0, prevY: 0, velX: 0, velY: 0 };

  renderer.domElement.style.cursor = 'grab';

  const onPointerDown = (e) => {
    drag.active = true;
    drag.prevX = e.clientX;
    drag.prevY = e.clientY;
    drag.velX = 0;
    drag.velY = 0;
    renderer.domElement.setPointerCapture(e.pointerId);
    renderer.domElement.style.cursor = 'grabbing';
  };

  const onPointerMove = (e) => {
    if (!drag.active || !model) return;
    const dx = e.clientX - drag.prevX;
    const dy = e.clientY - drag.prevY;
    drag.velX = dx;
    drag.velY = dy;
    model.rotation.y += dx * 0.008;
    model.rotation.x += dy * 0.008;
    drag.prevX = e.clientX;
    drag.prevY = e.clientY;
  };

  const onPointerUp = () => {
    drag.active = false;
    renderer.domElement.style.cursor = 'grab';
  };

  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointermove', onPointerMove);
  renderer.domElement.addEventListener('pointerup', onPointerUp);

  loader.load(
    modelPath,
    (gltf) => {
      const m = gltf.scene;
      const box = new THREE.Box3().setFromObject(m);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      m.scale.setScalar(5 / maxDim);

      const scaledBox = new THREE.Box3().setFromObject(m);
      const center = scaledBox.getCenter(new THREE.Vector3());
      m.position.sub(center);

      const group = new THREE.Group();
      group.add(m);
      group.position.x = offsetX;
      group.position.y = offsetY;

      model = group;
      scene.add(model);
    },
    undefined,
    (err) => console.error(`Error loading ${modelPath}:`, err)
  );

  let animFrameId;
  function animate() {
    animFrameId = requestAnimationFrame(animate);
    if (model && !drag.active) {
      drag.velX *= 0.95;
      drag.velY *= 0.95;
      model.rotation.y += drag.velX * 0.008;
      model.rotation.x += drag.velY * 0.008;
      if (Math.abs(drag.velX) < 0.01 && Math.abs(drag.velY) < 0.01) {
        model.rotation.y += 0.002;
      }
    }
    renderer.render(scene, camera);
  }
  animate();

  function dispose() {
    cancelAnimationFrame(animFrameId);
    renderer.domElement.removeEventListener('pointerdown', onPointerDown);
    renderer.domElement.removeEventListener('pointermove', onPointerMove);
    renderer.domElement.removeEventListener('pointerup', onPointerUp);
    renderer.dispose();
    if (wrapEl.contains(renderer.domElement)) {
      wrapEl.removeChild(renderer.domElement);
    }
  }

  return { resize, dispose };
}

function ModelViewer({ modelPath, offsetY = 0, offsetX = 0 }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const viewer = createViewer(wrapRef.current, modelPath, offsetY, offsetX);
    const handleResize = () => viewer.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      viewer.dispose();
    };
  }, [modelPath, offsetY, offsetX]);

  return <div ref={wrapRef} style={{ width: '100%', height: '100%' }} />;
}

const Space = () => {
  return (
    <div id="space" style={{ width: '100%', fontFamily: 'Courier New, monospace' }}>
      <br></br>
      <br></br>
      <h1 style={{ color: 'white', padding: '30px', textAlign: 'center' }}>Where I've Worked</h1>

      {/* --- Row 1: Three.js models, fixed height --- */}
      <div style={{ display: 'flex', width: '100%', height: '50vh' }}>

        <div style={{ flex: 1, position: 'relative'}}>
          <ModelViewer modelPath="/iss.glb" offsetY={1} />
        </div>

        <div style={{ flex: 1, position: 'relative'}}>
          <ModelViewer modelPath="/jwst.glb" />
        </div>

        <div style={{ flex: 1, position: 'relative' }}>
          <ModelViewer modelPath="/sat.glb" offsetY={-1} />
        </div>

      </div>

      {/* --- Row 2: Modals, all starting at the same Y --- */}
      <div style={{ display: 'flex', width: '100%', alignItems: 'flex-start' }}>

        <div style={{ flex: 1}}>
          <Windows95Modal boxWidth={400} location="Houston, TX" dates="05/2022-05/2024" icon={boeinglogo} iconWidth={2} iconHeight={32}>
            <h6><b>Software Engineer Intern</b></h6>
            <p><i>LZ Technology contractor for NASA's Int'l Space Station Software Development and Integration Laboratory.</i></p>
            <ul>
              <li>Implemented in C# and ASP.NET MVC administrative features for NASA asset management software.</li>
              <li>Updated an internal tools software with 300+ users that manages business operations of the Int'l Space Station.</li>
              <li>Deployed back end SQL Server functionality to forms using Entity Framework and seeding with JSON.</li>
            </ul>
          </Windows95Modal>
        </div>

        <div style={{ flex: 1}}>
          <Windows95Modal boxWidth={400} location="Boulder, CO" dates="06/2024-08/2024" icon={baelogo} iconWidth={2} iconHeight={32}>
            <h6><b>Systems Engineer Intern</b></h6>
            <p><i>Spacecraft embedded systems intern for the COSMOS Command and Control System</i></p>
            <ul>
              <li>Developed software using Ruby for a C2 system that communicates with multiple payload embedded systems.</li>
              <li>Built software in C++ to downlink temperature, humidity, and accelerometer data from a deployed payload.</li>
              <li>Designed a web interface to view RF data transmitted in real time collected by sensors onboard a payload.</li>
            </ul>
          </Windows95Modal>
        </div>

        <div style={{ flex: 1 }}>
          <Windows95Modal boxWidth={400} location="Boulder, CO" dates="08/2024-present" icon={baelogo} iconWidth={2} iconHeight={32}>
            <h6><b>Software Engineer I</b></h6>
            <p><i>Spacecraft embedded systems developer for the COSMOS Command and Control System</i></p>
            <ul>
              <li>Designed various telemetry CCSDS networking protocols in Golang for the COSMOS C2 ground system.</li>
              <li>Experience with developing software to graph telemetry and view space packet data.</li>
              <li>Provided support for mission planning for the WSF-M satellite.</li>
              <li>Developed the frontend for the COSMOS C2 ground system using Vue.js and the COSMOS API.</li>
            </ul>
          </Windows95Modal>
        </div>

      </div>
    </div>
  );
};

export default Space;